<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'vue-chartjs';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import IspMetricCard from './components/IspMetricCard.vue';
import IspBarChart from './components/IspBarChart.vue';
import IspChartjsLine from './components/IspChartjsLine.vue';
import { useIspReport } from './composables/useIspReport';
import { formatTime } from './composables/ispFormatters';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';
import './styles/isp-filters.css';

const store = useStore();
const { t } = useI18n();

const {
  customDateRange,
  selectedDateRange,
  data,
  error,
  fetchData,
  retry,
  onDateRangeChange,
  onFilterChange,
} = useIspReport({
  getter: 'ispReports/getAgentRanking',
  action: 'ispReports/fetchAgentRanking',
  filters: { teamId: '' },
});

// GK-HUB: Team filter (standardized — same pattern as IspFcr/IspPainel/IspMonitoramento)
const teams = computed(() => store.getters['teams/getTeams']);
const selectedTeamId = ref('');
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const userRole = computed(() =>
  getUserRole(currentUser.value, currentAccountId.value)
);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(userRole.value)
);

onMounted(() => {
  store.dispatch('teams/get');
});

const onTeamChange = e => {
  selectedTeamId.value = e.target.value;
  onFilterChange('teamId', e);
};
// GK-HUB: end team filter

// Auto-refresh every 60s
let refreshInterval = null;
onMounted(() => {
  refreshInterval = setInterval(fetchData, 60000);
});
onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
});

const isFetching = computed(() => data.value?.isFetching || false);
const reportError = computed(() => error.value);
const agents = computed(() => data.value?.data?.agents || []);
const summary = computed(() => data.value?.data?.summary || {});

// GK-HUB: Column sorting (default: score desc = original ranking)
const sortKey = ref('score');
const sortDir = ref('desc');

const toggleSort = key => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    // Default direction: desc for metrics, asc for name
    sortDir.value = key === 'agent_name' ? 'asc' : 'desc';
  }
};

const sortedAgents = computed(() => {
  const list = [...agents.value];
  const key = sortKey.value;
  const dir = sortDir.value === 'asc' ? 1 : -1;

  return list.sort((a, b) => {
    const va = a[key];
    const vb = b[key];
    if (typeof va === 'string') return dir * va.localeCompare(vb);
    return dir * ((va || 0) - (vb || 0));
  });
});

const sortIcon = key => {
  if (sortKey.value !== key) return 'i-lucide-arrow-up-down';
  return sortDir.value === 'asc' ? 'i-lucide-arrow-up' : 'i-lucide-arrow-down';
};
// GK-HUB: end column sorting

const scoreColor = score => {
  if (score >= 80) return 'text-n-teal-11';
  if (score >= 60) return 'text-n-blue-11';
  if (score >= 40) return 'text-amber-500';
  return 'text-n-ruby-11';
};

const scoreBgColor = score => {
  if (score >= 80) return 'bg-n-teal-9';
  if (score >= 60) return 'bg-n-blue-9';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-n-ruby-9';
};

const rankBadge = rank => {
  if (rank === 1) return { emoji: '\uD83E\uDD47', class: 'text-amber-400' };
  if (rank === 2) return { emoji: '\uD83E\uDD48', class: 'text-n-slate-9' };
  if (rank === 3) return { emoji: '\uD83E\uDD49', class: 'text-amber-600' };
  return null;
};

const availabilityDot = status => {
  if (status === 'online') return 'bg-n-teal-9';
  if (status === 'busy') return 'bg-amber-500';
  if (status === 'on_break') return 'bg-n-blue-9';
  return 'bg-n-slate-7';
};

// GK-HUB: Agent bar charts (Atendimentos + TMA por agente)
const atendBarItems = computed(() => {
  const list = [...agents.value].sort((a, b) => b.conversas - a.conversas);
  const max = Math.max(...list.map(a => a.conversas), 1);
  const colors = [
    'blue',
    'cyan',
    'dark',
    'blue-light',
    'cyan-light',
    'success',
    'warning',
  ];
  return list.map((a, i) => ({
    label: a.agent_name,
    value: a.conversas,
    displayValue: String(a.conversas),
    percentage: Math.round((a.conversas / max) * 100),
    color: colors[i % colors.length],
  }));
});

const TMA_TARGET_SECONDS = 600; // 10 min

const tmaBarItems = computed(() => {
  const list = [...agents.value].sort((a, b) => a.tma_seconds - b.tma_seconds);
  const max = Math.max(...list.map(a => a.tma_seconds), 1);
  return list.map(a => {
    let color = 'success';
    if (a.tma_seconds > TMA_TARGET_SECONDS * 2) color = 'error';
    else if (a.tma_seconds > TMA_TARGET_SECONDS) color = 'warning';
    return {
      label: a.agent_name,
      value: a.tma_seconds,
      displayValue: formatTime(a.tma_seconds),
      percentage: Math.round((a.tma_seconds / max) * 100),
      color,
    };
  });
});
// GK-HUB: end agent bar charts

// GK-HUB: Agent Detail slide-over
const selectedAgentId = ref(null);
const activeTab = ref('visao');

const agentDetail = computed(() => store.getters['ispReports/getAgentDetail']);
const detailData = computed(() => agentDetail.value?.data || null);
const detailFetching = computed(() => agentDetail.value?.isFetching || false);

const openAgentDetail = agent => {
  selectedAgentId.value = agent.agent_id;
  activeTab.value = 'visao';
  store.dispatch('ispReports/fetchAgentDetail', {
    agentId: agent.agent_id,
    from: customDateRange.value.from,
    to: customDateRange.value.to,
    teamId: selectedTeamId.value || undefined,
  });
};

const closeAgentDetail = () => {
  selectedAgentId.value = null;
  store.dispatch('ispReports/clearAgentDetail');
};

// ESC to close
const onEsc = e => {
  if (e.key === 'Escape') closeAgentDetail();
};
onMounted(() => window.addEventListener('keydown', onEsc));
onUnmounted(() => window.removeEventListener('keydown', onEsc));

const tabs = [
  { key: 'visao', label: 'ISP_REPORTS.AGENT_DETAIL.TAB_OVERVIEW' },
  { key: 'performance', label: 'ISP_REPORTS.AGENT_DETAIL.TAB_PERFORMANCE' },
  { key: 'pausas', label: 'ISP_REPORTS.AGENT_DETAIL.TAB_BREAKS' },
  { key: 'conversas', label: 'ISP_REPORTS.AGENT_DETAIL.TAB_CONVERSATIONS' },
];

// Overview: motivos for IspBarChart
const motivosBarItems = computed(() => {
  if (!detailData.value?.overview?.motivos) return [];
  const items = detailData.value.overview.motivos;
  const max = Math.max(...items.map(i => i.value), 1);
  const colors = ['blue', 'cyan', 'dark', 'blue-light', 'cyan-light'];
  return items.map((item, idx) => ({
    label: item.label,
    value: item.value,
    displayValue: String(item.value),
    percentage: Math.round((item.value / max) * 100),
    color: colors[idx % colors.length],
  }));
});

// Overview: canais for IspBarChart
const canaisBarItems = computed(() => {
  if (!detailData.value?.overview?.canais) return [];
  const items = detailData.value.overview.canais;
  const max = Math.max(...items.map(i => i.value), 1);
  const colors = ['blue', 'cyan', 'success', 'warning', 'dark'];
  return items.map((item, idx) => ({
    label: item.label,
    value: item.value,
    displayValue: String(item.value),
    percentage: Math.round((item.value / max) * 100),
    color: colors[idx % colors.length],
  }));
});

// Overview: volume hora chart data (Bar chart)
const volumeHoraData = computed(() => {
  const hours = detailData.value?.overview?.volume_hora || [];
  return {
    labels: hours.map(h => `${String(h.hora).padStart(2, '0')}h`),
    datasets: [
      {
        data: hours.map(h => h.count),
        backgroundColor: 'rgba(46, 134, 193, 0.6)',
        borderColor: 'rgba(46, 134, 193, 0.9)',
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  };
});

const volumeHoraOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { enabled: true } },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 9 }, color: '#64748b', maxRotation: 0 },
    },
    y: {
      grid: { color: 'rgba(255,255,255,0.04)' },
      ticks: { font: { size: 10 }, color: '#64748b' },
      beginAtZero: true,
    },
  },
};

// Overview: CSAT evolution for IspChartjsLine
const csatEvolucaoSeries = computed(() => {
  const points = detailData.value?.overview?.csat_evolucao || [];
  return [
    {
      name: 'CSAT',
      color: '#2E86C1',
      data: points.map(d => ({ label: d.label, value: d.value })),
    },
  ];
});

// Overview: Score ring SVG
const scoreCircumference = 2 * Math.PI * 40;
const scoreDashoffset = computed(() => {
  const score = detailData.value?.overview?.score || 0;
  return scoreCircumference - (score / 100) * scoreCircumference;
});

// Performance: TMA evolution for IspChartjsLine
const tmaEvolucaoSeries = computed(() => {
  const points = detailData.value?.performance?.tma_evolucao || [];
  return [
    {
      name: 'TMA',
      color: '#5DADE2',
      data: points.map(d => ({
        label: d.label,
        value: Math.round(d.value / 60),
      })),
    },
  ];
});

// Performance: comparison bars
const comparisonMetrics = computed(() => {
  const comp = detailData.value?.performance?.comparativo;
  if (!comp) return [];
  return [
    {
      label: 'TMA',
      agent: comp.tma?.agent || 0,
      team: comp.tma?.team || 0,
      format: v => formatTime(v),
      inverted: true,
    },
    {
      label: 'CSAT',
      agent: parseFloat(comp.csat?.agent) || 0,
      team: parseFloat(comp.csat?.team) || 0,
      format: v => Number(v || 0).toFixed(1),
      inverted: false,
    },
    {
      label: 'FCR',
      agent: parseFloat(comp.fcr?.agent) || 0,
      team: parseFloat(comp.fcr?.team) || 0,
      format: v => `${Number(v || 0).toFixed(1)}%`,
      inverted: false,
    },
  ];
});

const compBarWidth = (value, metric) => {
  const max = Math.max(metric.agent, metric.team, 1);
  return `${Math.max((value / max) * 100, 5)}%`;
};

// Pausas: breaks by day chart data
const pausasDiaData = computed(() => {
  const dias = detailData.value?.pausas?.por_dia || [];
  return {
    labels: dias.map(d => d.label),
    datasets: [
      {
        data: dias.map(d => Math.round(d.value / 60)),
        backgroundColor: 'rgba(245, 158, 11, 0.6)',
        borderColor: 'rgba(245, 158, 11, 0.9)',
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  };
});

const pausasDiaOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: ctx => `${ctx.parsed.y} min`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 10 }, color: '#64748b' },
    },
    y: {
      grid: { color: 'rgba(255,255,255,0.04)' },
      ticks: {
        font: { size: 10 },
        color: '#64748b',
        callback: v => `${v}m`,
      },
      beginAtZero: true,
    },
  },
};

// Conversas: status badge
const statusBadge = status => {
  const map = {
    resolved: { label: 'Resolvida', class: 'bg-n-teal-9/20 text-n-teal-11' },
    open: { label: 'Aberta', class: 'bg-n-blue-9/20 text-n-blue-11' },
    pending: {
      label: 'Pendente',
      class: 'bg-amber-500/20 text-amber-500',
    },
    snoozed: {
      label: 'Snoozed',
      class: 'bg-n-slate-7/20 text-n-slate-9',
    },
  };
  return (
    map[status] || { label: status, class: 'bg-n-slate-7/20 text-n-slate-9' }
  );
};

const formatDate = iso => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// vs team badge helper. isTime=true formats diff as "1h 30m" (TMA real \u00e9 em segundos).
const vsTeamBadge = (comparison, isTime = false) => {
  if (!comparison) return null;
  const diff = comparison.diff;
  if (diff === 0) return null;
  const arrow = comparison.better ? '\u2191' : '\u2193';
  const cls = comparison.better ? 'text-n-teal-11' : 'text-n-ruby-11';
  const value = isTime ? formatTime(Math.abs(diff)) : Math.abs(diff).toFixed(1);
  return { text: `${arrow} ${value}`, cls };
};
// GK-HUB: end Agent Detail slide-over
</script>

<template>
  <div class="p-6">
    <!-- GK-HUB: Standardized header (same pattern as IspFcr/IspConversas/IspPainel) -->
    <div class="mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.AGENT_RANKING.HEADER') }}
      </h1>
      <p class="text-[13px] text-n-slate-10 mt-0.5">
        {{ t('ISP_REPORTS.AGENT_RANKING.SUBTITLE') }}
      </p>
    </div>

    <!-- GK-HUB: Standardized filter bar (isp-select-wrap pattern) -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <!-- GK-HUB: Team filter (admin/manager only) — isp-select-wrap pattern -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select
          v-model="selectedTeamId"
          class="isp-select"
          @change="onTeamChange"
        >
          <option value="">
            {{ t('ISP_REPORTS.FILTERS.ALL_TEAMS') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <!-- GK-HUB: end team filter -->
    </div>
    <!-- GK-HUB: end standardized header/filters -->

    <!-- Loading -->
    <woot-loading-state
      v-if="isFetching && !data.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error -->
    <div
      v-else-if="reportError && !data.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2 cursor-pointer"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="data.data">
      <!-- Summary Cards (5 cols — includes Agentes Online) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <IspMetricCard
          :label="t('ISP_REPORTS.AGENT_RANKING.TOTAL_AGENTS')"
          :value="summary.total_agents || 0"
          :loading="isFetching"
        />
        <!-- GK-HUB: Agentes Online KPI -->
        <IspMetricCard
          :label="t('ISP_REPORTS.AGENT_RANKING.ONLINE_NOW')"
          :value="summary.online_count || 0"
          value-color="text-n-teal-11"
          :loading="isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.AGENT_RANKING.AVG_FCR')"
          :value="`${summary.avg_fcr || 0}%`"
          value-color="text-n-teal-11"
          :loading="isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.AGENT_RANKING.AVG_TMA')"
          :value="formatTime(summary.avg_tma || 0)"
          value-color="text-n-blue-11"
          :loading="isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.AGENT_RANKING.AVG_CSAT')"
          :value="summary.avg_csat || 0"
          value-color="text-amber-500"
          :loading="isFetching"
        />
      </div>

      <!-- Score formula hint -->
      <div
        class="mb-4 px-3 py-2 bg-n-solid-2 border border-n-weak rounded-lg text-[11px] text-n-slate-9 flex items-center gap-2"
      >
        <span class="i-lucide-info w-3.5 h-3.5 flex-shrink-0" />
        {{ t('ISP_REPORTS.AGENT_RANKING.SCORE_FORMULA') }}
      </div>

      <!-- Agent Table -->
      <div
        class="bg-n-solid-2 border border-n-weak rounded-[10px] overflow-hidden"
      >
        <div class="overflow-x-auto">
          <table class="w-full text-[13px]">
            <!-- GK-HUB: Sortable column headers -->
            <thead>
              <tr class="border-b border-n-weak bg-n-solid-3">
                <th
                  class="isp-th-sort text-center w-10 px-2"
                  @click="toggleSort('rank')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_RANK') }}
                  <span :class="sortIcon('rank')" class="isp-sort-icon" />
                </th>
                <th
                  class="isp-th-sort text-left px-3"
                  @click="toggleSort('agent_name')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_AGENT') }}
                  <span :class="sortIcon('agent_name')" class="isp-sort-icon" />
                </th>
                <th
                  class="isp-th-sort isp-th-right px-3"
                  @click="toggleSort('conversas')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_CONVERSATIONS') }}
                  <span :class="sortIcon('conversas')" class="isp-sort-icon" />
                </th>
                <th
                  class="isp-th-sort isp-th-right px-3"
                  @click="toggleSort('resolvidas')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_RESOLVED') }}
                  <span :class="sortIcon('resolvidas')" class="isp-sort-icon" />
                </th>
                <th
                  class="isp-th-sort isp-th-right px-3"
                  @click="toggleSort('fcr_rate')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_FCR') }}
                  <span :class="sortIcon('fcr_rate')" class="isp-sort-icon" />
                </th>
                <th
                  class="isp-th-sort isp-th-right px-3"
                  @click="toggleSort('tma_seconds')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_TMA') }}
                  <span
                    :class="sortIcon('tma_seconds')"
                    class="isp-sort-icon"
                  />
                </th>
                <th
                  class="isp-th-sort isp-th-right px-3"
                  @click="toggleSort('tme_seconds')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_TME') }}
                  <span
                    :class="sortIcon('tme_seconds')"
                    class="isp-sort-icon"
                  />
                </th>
                <th
                  class="isp-th-sort isp-th-right px-3"
                  @click="toggleSort('csat_avg')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_CSAT') }}
                  <span :class="sortIcon('csat_avg')" class="isp-sort-icon" />
                </th>
                <th
                  class="isp-th-sort isp-th-right px-3"
                  @click="toggleSort('break_minutes')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_BREAK') }}
                  <span
                    :class="sortIcon('break_minutes')"
                    class="isp-sort-icon"
                  />
                </th>
                <th
                  class="isp-th-sort isp-th-right px-3"
                  @click="toggleSort('score')"
                >
                  {{ t('ISP_REPORTS.AGENT_RANKING.COL_SCORE') }}
                  <span :class="sortIcon('score')" class="isp-sort-icon" />
                </th>
              </tr>
            </thead>
            <!-- GK-HUB: end sortable headers -->
            <tbody>
              <!-- GK-HUB: Agent row — cursor-pointer + click opens detail slide-over -->
              <tr
                v-for="agent in sortedAgents"
                :key="agent.agent_id"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors cursor-pointer"
                @click="openAgentDetail(agent)"
              >
                <!-- Rank -->
                <td class="px-2 py-3 text-center">
                  <span
                    v-if="rankBadge(agent.rank)"
                    class="text-base"
                    :class="rankBadge(agent.rank).class"
                  >
                    {{ rankBadge(agent.rank).emoji }}
                  </span>
                  <span v-else class="text-n-slate-9 font-medium">
                    {{ agent.rank }}
                  </span>
                </td>

                <!-- Agent -->
                <td class="px-3 py-3">
                  <div class="flex items-center gap-2">
                    <div class="relative flex-shrink-0">
                      <img
                        v-if="agent.avatar_url"
                        :src="agent.avatar_url"
                        :alt="agent.agent_name"
                        class="w-7 h-7 rounded-full object-cover"
                      />
                      <div
                        v-else
                        class="w-7 h-7 rounded-full bg-n-solid-3 flex items-center justify-center text-[10px] font-semibold text-n-slate-10"
                      >
                        {{
                          agent.agent_name
                            ? agent.agent_name.charAt(0).toUpperCase()
                            : '?'
                        }}
                      </div>
                      <span
                        class="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border-[1.5px] border-n-solid-2"
                        :class="availabilityDot(agent.availability)"
                      />
                    </div>
                    <span
                      class="font-medium text-n-slate-12 truncate max-w-[140px]"
                    >
                      {{ agent.agent_name }}
                    </span>
                    <!-- GK-HUB: IA badge next to agent name -->
                    <span
                      v-if="agent.is_ia_agent"
                      class="gk-ia-badge flex-shrink-0"
                    >
                      {{ $t('CONVERSATION.IA_BADGE') }}
                    </span>
                  </div>
                </td>

                <!-- Conversations -->
                <td class="px-3 py-3 text-right text-n-slate-11 tabular-nums">
                  {{ agent.conversas }}
                </td>

                <!-- Resolved -->
                <td class="px-3 py-3 text-right text-n-slate-11 tabular-nums">
                  {{ agent.resolvidas }}
                </td>

                <!-- FCR -->
                <td class="px-3 py-3 text-right tabular-nums">
                  <span
                    :class="
                      agent.fcr_rate >= 80
                        ? 'text-n-teal-11'
                        : agent.fcr_rate >= 60
                          ? 'text-n-blue-11'
                          : 'text-n-ruby-11'
                    "
                  >
                    {{ agent.fcr_rate }}%
                  </span>
                </td>

                <!-- TMA (real — sum de assignee_segments) -->
                <td
                  class="px-3 py-3 text-right text-n-slate-11 tabular-nums whitespace-nowrap"
                >
                  {{ formatTime(agent.tma_seconds) }}
                </td>

                <!-- TME -->
                <td
                  class="px-3 py-3 text-right text-n-slate-11 tabular-nums whitespace-nowrap"
                >
                  {{ formatTime(agent.tme_seconds) }}
                </td>

                <!-- CSAT -->
                <td class="px-3 py-3 text-right tabular-nums">
                  <span
                    :class="
                      agent.csat_avg >= 4
                        ? 'text-n-teal-11'
                        : agent.csat_avg >= 3
                          ? 'text-amber-500'
                          : 'text-n-ruby-11'
                    "
                  >
                    {{ agent.csat_avg || '-' }}
                  </span>
                </td>

                <!-- Break -->
                <td
                  class="px-3 py-3 text-right text-n-slate-9 tabular-nums whitespace-nowrap"
                >
                  {{ formatTime(agent.break_minutes * 60) }}
                </td>

                <!-- Score (pill badge) -->
                <td class="px-3 py-3 text-right">
                  <span
                    class="inline-flex items-center gap-1.5 font-semibold tabular-nums"
                    :class="scoreColor(agent.score)"
                  >
                    <span
                      class="w-2 h-2 rounded-full flex-shrink-0"
                      :class="scoreBgColor(agent.score)"
                    />
                    {{ agent.score }}
                  </span>
                </td>
              </tr>
              <!-- GK-HUB: end agent row -->
            </tbody>
          </table>
        </div>

        <!-- Empty state inside table -->
        <div
          v-if="!agents.length && !isFetching"
          class="flex flex-col items-center justify-center py-16 gap-2"
        >
          <span class="i-lucide-users w-8 h-8 text-n-slate-7" />
          <p class="text-sm text-n-slate-9">
            {{ t('ISP_REPORTS.AGENT_RANKING.NO_DATA') }}
          </p>
        </div>
      </div>

      <!-- GK-HUB: Atendimentos por agente + TMA por agente (horizontal bars) -->
      <div
        v-if="agents.length"
        class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6"
      >
        <IspBarChart
          :title="t('ISP_REPORTS.AGENT_RANKING.CONVS_BY_AGENT')"
          :items="atendBarItems"
          label-width="120px"
        />
        <IspBarChart
          :title="t('ISP_REPORTS.AGENT_RANKING.TMA_BY_AGENT')"
          :items="tmaBarItems"
          label-width="120px"
        />
      </div>
      <!-- GK-HUB: end agent bar charts -->
    </template>

    <!-- Empty State (no data at all) -->
    <div
      v-if="!isFetching && !reportError && !data.data"
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.AGENT_RANKING.NO_DATA') }}
      </h3>
    </div>

    <!-- GK-HUB: Agent Detail slide-over overlay -->
    <Transition name="fade">
      <div
        v-if="selectedAgentId"
        class="fixed inset-0 bg-black/30 z-40"
        @click="closeAgentDetail"
      />
    </Transition>

    <!-- GK-HUB: Agent Detail slide-over panel -->
    <Transition name="slide">
      <div
        v-if="selectedAgentId"
        class="fixed top-0 right-0 z-50 h-full w-full max-w-4xl bg-n-solid-2 border-l border-n-weak shadow-xl overflow-y-auto"
        @click.stop
      >
        <!-- Loading -->
        <div
          v-if="detailFetching && !detailData"
          class="flex items-center justify-center h-64"
        >
          <woot-loading-state :message="t('ISP_REPORTS.LOADING')" />
        </div>

        <template v-if="detailData">
          <!-- Header with avatar + name + badges -->
          <div
            class="flex items-center justify-between px-5 py-4 border-b border-n-weak sticky top-0 bg-n-solid-2 z-10"
          >
            <div class="flex items-center gap-3">
              <div class="relative">
                <img
                  v-if="detailData.agent.avatar_url"
                  :src="detailData.agent.avatar_url"
                  class="w-10 h-10 rounded-full"
                />
                <div
                  v-else
                  class="w-10 h-10 rounded-full bg-n-solid-3 flex items-center justify-center text-sm font-semibold text-n-slate-10"
                >
                  {{ detailData.agent.name?.charAt(0)?.toUpperCase() }}
                </div>
                <span
                  class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-n-solid-2"
                  :class="availabilityDot(detailData.agent.availability)"
                />
              </div>
              <div>
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ detailData.agent.name }}
                </h2>
                <div class="flex items-center gap-2 mt-0.5">
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
                  <span class="text-xs text-n-slate-9">Score</span>
                  <span
                    class="text-xs font-semibold"
                    :class="scoreColor(detailData.overview.score)"
                  >
                    {{ detailData.overview.score }}
                  </span>
                </div>
              </div>
            </div>
            <button
              class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-n-solid-3 transition-colors cursor-pointer"
              @click="closeAgentDetail"
            >
              <span class="i-lucide-x w-4 h-4 text-n-slate-10" />
            </button>
          </div>

          <!-- Tabs -->
          <div
            class="flex border-b border-n-weak px-5 sticky top-[65px] bg-n-solid-2 z-10"
          >
            <button
              v-for="tab in tabs"
              :key="tab.key"
              class="px-3 py-2.5 text-[12px] font-medium transition-colors border-b-2 -mb-[1px] cursor-pointer"
              :class="
                activeTab === tab.key
                  ? 'text-n-blue-11 border-n-blue-11'
                  : 'text-n-slate-9 border-transparent hover:text-n-slate-11'
              "
              @click="activeTab = tab.key"
            >
              {{ t(tab.label) }}
            </button>
          </div>

          <!-- Tab content area -->
          <div class="p-5">
            <!-- ═══════ TAB: VISAO GERAL ═══════ -->
            <div v-if="activeTab === 'visao'">
              <!-- 4 KPI cards with vs team badge -->
              <div class="grid grid-cols-2 gap-3 mb-5">
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.CONVERSATIONS') }}
                  </div>
                  <div class="text-xl font-bold text-n-slate-12">
                    {{ detailData.overview.conversas }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.TMA') }}
                  </div>
                  <div class="text-xl font-bold text-n-slate-12">
                    {{ formatTime(detailData.overview.tma_seconds) }}
                  </div>
                  <div
                    v-if="vsTeamBadge(detailData.overview.vs_team?.tma, true)"
                    class="text-[10px] font-medium mt-0.5"
                    :class="
                      vsTeamBadge(detailData.overview.vs_team.tma, true).cls
                    "
                  >
                    {{
                      vsTeamBadge(detailData.overview.vs_team.tma, true).text
                    }}
                    {{ t('ISP_REPORTS.AGENT_DETAIL.VS_TEAM') }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.CSAT') }}
                  </div>
                  <div class="text-xl font-bold text-n-slate-12">
                    {{ detailData.overview.csat_avg || '-' }}
                  </div>
                  <div
                    v-if="vsTeamBadge(detailData.overview.vs_team?.csat)"
                    class="text-[10px] font-medium mt-0.5"
                    :class="vsTeamBadge(detailData.overview.vs_team.csat).cls"
                  >
                    {{ vsTeamBadge(detailData.overview.vs_team.csat).text }}
                    {{ t('ISP_REPORTS.AGENT_DETAIL.VS_TEAM') }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.FCR') }}
                  </div>
                  <div class="text-xl font-bold text-n-slate-12">
                    {{ detailData.overview.fcr_rate }}%
                  </div>
                  <div
                    v-if="vsTeamBadge(detailData.overview.vs_team?.fcr)"
                    class="text-[10px] font-medium mt-0.5"
                    :class="vsTeamBadge(detailData.overview.vs_team.fcr).cls"
                  >
                    {{ vsTeamBadge(detailData.overview.vs_team.fcr).text }}
                    {{ t('ISP_REPORTS.AGENT_DETAIL.VS_TEAM') }}
                  </div>
                </div>
              </div>

              <!-- Row 2: Top motivos + Volume por hora -->
              <div class="grid grid-cols-2 gap-4 mb-5">
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.TOP_MOTIVOS') }}
                  </div>
                  <IspBarChart
                    v-if="motivosBarItems.length"
                    :items="motivosBarItems"
                    label-width="110px"
                  />
                  <div v-else class="text-xs text-n-slate-9 py-4 text-center">
                    -
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.VOLUME_HORA') }}
                  </div>
                  <div class="h-[180px]">
                    <Bar :data="volumeHoraData" :options="volumeHoraOptions" />
                  </div>
                </div>
              </div>

              <!-- Row 3: Score ring + CSAT evolution + Canais -->
              <div class="grid grid-cols-3 gap-4">
                <!-- Score ring -->
                <div class="ad-inner-card flex flex-col items-center">
                  <div class="ad-section-label text-center w-full">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.SCORE_LABEL') }}
                  </div>
                  <svg viewBox="0 0 100 100" class="w-24 h-24 -rotate-90">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="rgba(255,255,255,0.06)"
                      stroke-width="8"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      :stroke="
                        detailData.overview.score >= 80
                          ? '#10b981'
                          : detailData.overview.score >= 60
                            ? '#2E86C1'
                            : detailData.overview.score >= 40
                              ? '#f59e0b'
                              : '#ef4444'
                      "
                      stroke-width="8"
                      stroke-linecap="round"
                      :stroke-dasharray="scoreCircumference"
                      :stroke-dashoffset="scoreDashoffset"
                      class="score-ring-circle"
                    />
                  </svg>
                  <div
                    class="text-lg font-bold mt-1"
                    :class="scoreColor(detailData.overview.score)"
                  >
                    {{ detailData.overview.score }}
                  </div>
                </div>

                <!-- CSAT evolution -->
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.CSAT_EVOLUTION') }}
                  </div>
                  <IspChartjsLine
                    v-if="csatEvolucaoSeries[0]?.data?.length"
                    :series="csatEvolucaoSeries"
                    :height="140"
                    :show-legend="false"
                  />
                  <div v-else class="text-xs text-n-slate-9 py-4 text-center">
                    -
                  </div>
                </div>

                <!-- Canais -->
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.CHANNELS') }}
                  </div>
                  <IspBarChart
                    v-if="canaisBarItems.length"
                    :items="canaisBarItems"
                    label-width="90px"
                  />
                  <div v-else class="text-xs text-n-slate-9 py-4 text-center">
                    -
                  </div>
                </div>
              </div>
            </div>

            <!-- ═══════ TAB: PERFORMANCE ═══════ -->
            <div v-if="activeTab === 'performance'">
              <!-- KPI cards (4 cols like preview) -->
              <div class="grid grid-cols-4 gap-3 mb-5">
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.RESOLVED') }}
                  </div>
                  <div class="text-xl font-bold text-n-teal-11">
                    {{ detailData.performance.resolvidas }}
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-0.5">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.IN_PERIOD') }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.NOT_RESOLVED') }}
                  </div>
                  <div class="text-xl font-bold text-n-ruby-11">
                    {{ detailData.performance.nao_resolvidas }}
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-0.5">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.OPEN_OR_PENDING') }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.IA_TRANSFERS') }}
                  </div>
                  <div class="text-xl font-bold text-n-blue-11">
                    {{ detailData.performance.ia_transfers || 0 }}
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-0.5">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.TRANSFERS') }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.TME') }}
                  </div>
                  <div class="text-xl font-bold text-amber-500">
                    {{ formatTime(detailData.performance.tme_seconds) }}
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-0.5">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.CLIENT_WAIT') }}
                  </div>
                </div>
              </div>

              <!-- Comparativo vs time -->
              <div class="ad-inner-card mb-5">
                <div class="ad-section-label">
                  {{ t('ISP_REPORTS.AGENT_DETAIL.COMPARISON') }}
                </div>
                <div class="space-y-4">
                  <div v-for="metric in comparisonMetrics" :key="metric.label">
                    <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
                    <div class="text-xs font-medium text-n-slate-10 mb-1.5">
                      {{ metric.label }}
                    </div>
                    <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
                    <div class="space-y-1">
                      <div class="flex items-center gap-2">
                        <span
                          class="text-[10px] text-n-slate-9 w-12 flex-shrink-0"
                        >
                          {{ detailData.agent.name?.split(' ')[0] }}
                        </span>
                        <div
                          class="h-5 rounded bg-n-blue-9/60 transition-all duration-500"
                          :style="{ width: compBarWidth(metric.agent, metric) }"
                        />
                        <span
                          class="text-[11px] font-semibold text-n-slate-12 flex-shrink-0"
                        >
                          {{ metric.format(metric.agent) }}
                        </span>
                      </div>
                      <div class="flex items-center gap-2">
                        <span
                          class="text-[10px] text-n-slate-9 w-12 flex-shrink-0"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.VS_TEAM') }}
                        </span>
                        <div
                          class="h-5 rounded bg-n-slate-7/40 transition-all duration-500"
                          :style="{ width: compBarWidth(metric.team, metric) }"
                        />
                        <span
                          class="text-[11px] font-medium text-n-slate-10 flex-shrink-0"
                        >
                          {{ metric.format(metric.team) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- SLA First Response -->
              <div class="grid grid-cols-2 gap-4 mb-5">
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.SLA_OK') }}
                  </div>
                  <div class="text-lg font-bold text-n-teal-11 mb-2">
                    {{ detailData.performance.sla_ok_pct }}%
                  </div>
                  <div
                    class="w-full h-1.5 bg-n-solid-3 rounded-full overflow-hidden"
                  >
                    <div
                      class="h-full bg-n-teal-9 rounded-full sla-bar"
                      :style="{
                        width: `${detailData.performance.sla_ok_pct}%`,
                      }"
                    />
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-1">
                    {{ detailData.performance.sla_ok_count }}
                    {{
                      t('ISP_REPORTS.AGENT_DETAIL.CONVERSATIONS').toLowerCase()
                    }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.SLA_NOK') }}
                  </div>
                  <div class="text-lg font-bold text-n-ruby-11 mb-2">
                    {{ detailData.performance.sla_nok_pct }}%
                  </div>
                  <div
                    class="w-full h-1.5 bg-n-solid-3 rounded-full overflow-hidden"
                  >
                    <div
                      class="h-full bg-n-ruby-9 rounded-full sla-bar"
                      :style="{
                        width: `${detailData.performance.sla_nok_pct}%`,
                      }"
                    />
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-1">
                    {{ detailData.performance.sla_nok_count }}
                    {{
                      t('ISP_REPORTS.AGENT_DETAIL.CONVERSATIONS').toLowerCase()
                    }}
                  </div>
                </div>
              </div>

              <!-- SLA distribution breakdown -->
              <div
                v-if="detailData.performance.sla_distribution?.length"
                class="ad-inner-card mb-5"
              >
                <div class="ad-section-label">
                  {{ t('ISP_REPORTS.AGENT_DETAIL.SLA_FIRST_RESPONSE') }}
                </div>
                <div class="space-y-2">
                  <div
                    v-for="sla in detailData.performance.sla_distribution"
                    :key="sla.label"
                    class="flex items-center gap-2"
                  >
                    <span
                      class="text-[11px] text-n-slate-10 w-14 flex-shrink-0 text-right"
                    >
                      {{ sla.label }}
                    </span>
                    <div
                      class="flex-1 h-4 bg-n-solid-3 rounded overflow-hidden"
                    >
                      <div
                        class="h-full rounded"
                        :style="{
                          width: `${Math.max(sla.pct, 2)}%`,
                          background:
                            sla.pct >= 20
                              ? 'rgba(46, 134, 193, 0.7)'
                              : 'rgba(46, 134, 193, 0.4)',
                        }"
                      />
                    </div>
                    <span
                      class="text-[11px] text-n-slate-11 w-16 flex-shrink-0 text-right"
                    >
                      {{ sla.count }}
                      ({{ sla.pct }}%)
                    </span>
                  </div>
                </div>
              </div>

              <!-- TMA evolution -->
              <div class="ad-inner-card">
                <div class="ad-section-label">
                  {{ t('ISP_REPORTS.AGENT_DETAIL.TMA_EVOLUTION') }}
                </div>
                <IspChartjsLine
                  v-if="tmaEvolucaoSeries[0]?.data?.length"
                  :series="tmaEvolucaoSeries"
                  :height="180"
                  :show-legend="false"
                />
                <div v-else class="text-xs text-n-slate-9 py-4 text-center">
                  -
                </div>
              </div>
            </div>

            <!-- ═══════ TAB: PAUSAS & JORNADA ═══════ -->
            <div v-if="activeTab === 'pausas'">
              <!-- KPI cards (4 cols like preview) -->
              <div class="grid grid-cols-4 gap-3 mb-5">
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.ONLINE_TIME') }}
                  </div>
                  <div class="text-xl font-bold text-n-teal-11">
                    {{ formatTime(detailData.pausas.online_seconds || 0) }}
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-0.5">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.IN_SHIFT') }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.TOTAL_BREAK') }}
                  </div>
                  <div class="text-xl font-bold text-amber-500">
                    {{ formatTime(detailData.pausas.total_seconds) }}
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-0.5">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.ACCUMULATED') }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.BREAK_COUNT') }}
                  </div>
                  <div class="text-xl font-bold text-n-slate-12">
                    {{ detailData.pausas.qtd_pausas }}
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-0.5">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.IN_PERIOD') }}
                  </div>
                </div>
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.AVG_BREAK') }}
                  </div>
                  <div class="text-xl font-bold text-n-slate-12">
                    {{ formatTime(detailData.pausas.media_seconds) }}
                  </div>
                  <div class="text-[10px] text-n-slate-9 mt-0.5">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.PER_OCCURRENCE') }}
                  </div>
                </div>
              </div>

              <!-- Row 2: Pausas por motivo + Timeline hoje -->
              <div class="grid grid-cols-2 gap-4 mb-5">
                <!-- Pausas por motivo -->
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.BREAK_BY_REASON') }}
                  </div>
                  <div
                    v-if="detailData.pausas.por_motivo?.length"
                    class="space-y-2.5"
                  >
                    <div
                      v-for="motivo in detailData.pausas.por_motivo"
                      :key="motivo.label"
                    >
                      <div
                        class="flex items-center justify-between text-[11px] mb-1"
                      >
                        <span class="text-n-slate-11 truncate max-w-[60%]">
                          {{ motivo.label }}
                        </span>
                        <span class="text-n-slate-9">
                          {{ formatTime(motivo.seconds) }}
                          ({{ motivo.pct }}%)
                        </span>
                      </div>
                      <div
                        class="w-full h-1.5 bg-n-solid-3 rounded-full overflow-hidden"
                      >
                        <!-- eslint-disable vue/no-static-inline-styles -->
                        <div
                          class="h-full rounded-full"
                          :style="{
                            width: `${Math.max(motivo.pct, 3)}%`,
                            background:
                              'linear-gradient(to right, rgba(245,158,11,0.6), rgba(245,158,11,0.9))',
                          }"
                        />
                        <!-- eslint-enable vue/no-static-inline-styles -->
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-xs text-n-slate-9 py-4 text-center">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.NO_BREAKS') }}
                  </div>
                </div>

                <!-- Timeline hoje -->
                <div class="ad-inner-card">
                  <div class="ad-section-label">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.TIMELINE_TODAY') }}
                  </div>
                  <div
                    v-if="detailData.pausas.timeline_hoje?.length"
                    class="space-y-1.5 max-h-[250px] overflow-y-auto"
                  >
                    <div
                      v-for="(block, idx) in detailData.pausas.timeline_hoje"
                      :key="idx"
                      class="timeline-block"
                      :class="
                        block.type === 'pausa'
                          ? 'timeline-pausa'
                          : 'timeline-online'
                      "
                    >
                      <div class="flex items-center justify-between">
                        <span class="font-medium">{{ block.label }}</span>
                        <span class="opacity-70">
                          {{ block.start }} - {{ block.end }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-xs text-n-slate-9 py-4 text-center">
                    {{ t('ISP_REPORTS.AGENT_DETAIL.NO_BREAKS') }}
                  </div>
                </div>
              </div>

              <!-- Pausas por dia (bar chart) -->
              <div class="ad-inner-card">
                <div class="ad-section-label">
                  {{ t('ISP_REPORTS.AGENT_DETAIL.BREAK_BY_DAY') }}
                </div>
                <div v-if="detailData.pausas.por_dia?.length" class="h-[180px]">
                  <Bar :data="pausasDiaData" :options="pausasDiaOptions" />
                </div>
                <div v-else class="text-xs text-n-slate-9 py-4 text-center">
                  {{ t('ISP_REPORTS.AGENT_DETAIL.NO_BREAKS') }}
                </div>
              </div>
            </div>

            <!-- ═══════ TAB: ULTIMAS CONVERSAS ═══════ -->
            <div v-if="activeTab === 'conversas'">
              <div class="ad-section-label mb-3">
                {{ t('ISP_REPORTS.AGENT_DETAIL.RECENT_CONVS') }}
              </div>
              <div
                v-if="detailData.conversas?.length"
                class="bg-n-solid-2 border border-n-weak rounded-[10px] overflow-hidden"
              >
                <div class="overflow-x-auto">
                  <table class="w-full text-[12px]">
                    <thead>
                      <tr class="border-b border-n-weak bg-n-solid-3">
                        <th
                          class="text-left px-3 py-2 text-[11px] font-semibold text-n-slate-9"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.COL_PROTOCOL') }}
                        </th>
                        <th
                          class="text-left px-3 py-2 text-[11px] font-semibold text-n-slate-9"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.COL_CHANNEL') }}
                        </th>
                        <th
                          class="text-left px-3 py-2 text-[11px] font-semibold text-n-slate-9"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.COL_CONTACT') }}
                        </th>
                        <th
                          class="text-left px-3 py-2 text-[11px] font-semibold text-n-slate-9"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.COL_MOTIVO') }}
                        </th>
                        <th
                          class="text-right px-3 py-2 text-[11px] font-semibold text-n-slate-9"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.COL_TMA') }}
                        </th>
                        <th
                          class="text-center px-3 py-2 text-[11px] font-semibold text-n-slate-9"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.COL_CSAT') }}
                        </th>
                        <th
                          class="text-center px-3 py-2 text-[11px] font-semibold text-n-slate-9"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.COL_STATUS') }}
                        </th>
                        <th
                          class="text-right px-3 py-2 text-[11px] font-semibold text-n-slate-9"
                        >
                          {{ t('ISP_REPORTS.AGENT_DETAIL.COL_DATE') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="conv in detailData.conversas"
                        :key="conv.display_id"
                        class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors"
                      >
                        <td class="px-3 py-2.5 text-n-blue-11 font-medium">
                          #{{ conv.display_id }}
                        </td>
                        <td class="px-3 py-2.5 text-n-slate-10">
                          {{ conv.channel_name }}
                        </td>
                        <td
                          class="px-3 py-2.5 text-n-slate-11 truncate max-w-[120px]"
                        >
                          {{ conv.contact_name }}
                        </td>
                        <td
                          class="px-3 py-2.5 text-n-slate-10 truncate max-w-[100px]"
                        >
                          {{ conv.motivo }}
                        </td>
                        <td
                          class="px-3 py-2.5 text-right text-n-slate-11 tabular-nums whitespace-nowrap"
                        >
                          {{
                            conv.tma_seconds
                              ? formatTime(conv.tma_seconds)
                              : '-'
                          }}
                        </td>
                        <td class="px-3 py-2.5 text-center">
                          <span
                            v-if="conv.csat_rating"
                            class="text-amber-500 font-semibold"
                          >
                            {{ conv.csat_rating }}
                          </span>
                          <span v-else class="text-n-slate-7">-</span>
                        </td>
                        <td class="px-3 py-2.5 text-center">
                          <span
                            class="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium"
                            :class="statusBadge(conv.status).class"
                          >
                            {{ statusBadge(conv.status).label }}
                          </span>
                        </td>
                        <td
                          class="px-3 py-2.5 text-right text-n-slate-9 whitespace-nowrap tabular-nums"
                        >
                          {{ formatDate(conv.created_at) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div
                v-else
                class="flex flex-col items-center justify-center py-12 gap-2"
              >
                <span class="i-lucide-message-circle w-8 h-8 text-n-slate-7" />
                <p class="text-sm text-n-slate-9">
                  {{ t('ISP_REPORTS.AGENT_DETAIL.NO_CONVERSATIONS') }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </Transition>
    <!-- GK-HUB: end Agent Detail slide-over -->
  </div>
</template>

<!-- GK-HUB: Standardized select wrapper + DatePicker z-index fix -->
<style scoped>
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

/* ── Sortable table headers ── */
.isp-th-sort {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding-top: 10px;
  padding-bottom: 10px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition: color 150ms ease;
}
.isp-th-sort:hover {
  color: #94a3b8;
}
.isp-th-right {
  text-align: right;
}
.isp-sort-icon {
  display: inline-block;
  width: 12px;
  height: 12px;
  vertical-align: middle;
  margin-left: 2px;
  opacity: 0.4;
  transition: opacity 150ms ease;
}
.isp-th-sort:hover .isp-sort-icon {
  opacity: 0.7;
}

/* DatePicker calendar z-index fix (prevent clipping) */
:deep(.date-picker) {
  position: relative;
  z-index: 20;
}
:deep(.date-picker .datepicker--container),
:deep(.date-picker [class*='absolute']) {
  z-index: 50 !important;
}
:deep(.daterange-picker--container),
:deep(.datepicker--container) {
  z-index: 50 !important;
}

/* ── GK-HUB: Slide-over transitions (same pattern as IspConversas) ── */
.slide-enter-active {
  transition: transform 300ms ease-out;
}
.slide-leave-active {
  transition: transform 200ms ease-in;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
.fade-enter-active {
  transition: opacity 300ms ease-out;
}
.fade-leave-active {
  transition: opacity 200ms ease-in;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── Agent detail inner cards ── */
.ad-inner-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 14px;
}
.ad-section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 8px;
}

/* Score ring SVG */
.score-ring-circle {
  transition: stroke-dashoffset 1s ease;
}

/* Timeline block */
.timeline-block {
  border-radius: 4px;
  min-height: 24px;
  font-size: 10px;
  padding: 4px 6px;
}
.timeline-online {
  background: rgba(16, 185, 129, 0.15);
  border-left: 3px solid #10b981;
}
.timeline-pausa {
  background: rgba(245, 158, 11, 0.15);
  border-left: 3px solid #f59e0b;
}

/* SLA progress bars */
.sla-bar {
  transition: width 600ms ease;
}
</style>

<!-- GK-HUB: Dark mode overrides for standardized selects (unscoped for body.dark targeting) -->
<style>
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
</style>
